import React, { useState, useCallback } from 'react';
import { api, getApiErrorMessage } from '../lib/api';

const ALERT_COLORS = {
  NOMINAL: '#818CF8',
  ELEVATED: '#F59E0B',
  DISRUPTIVE: '#F97316',
  CRITICAL: '#EF4444',
};

const MODES = [
  { key: 'briefing', label: 'BRIEFING' },
  { key: 'explain', label: 'EXPLAIN' },
  { key: 'compare', label: 'COMPARE' },
  { key: 'watch', label: 'WATCH' },
];

export default function VegaRightPanel({ insight, language }) {
  const [mode, setMode] = useState('briefing');
  const [generating, setGenerating] = useState(false);
  const [scriptResult, setScriptResult] = useState(null);
  const [error, setError] = useState(null);
  const [question, setQuestion] = useState('');

  const handleGenerate = useCallback(async () => {
    if (!insight) return;
    setGenerating(true);
    setError(null);
    setScriptResult(null);

    try {
      let res;
      if (mode === 'briefing') {
        res = await api.post('/api/vega/briefing', null, {
          params: { domain: insight.domain, mode: 'infrastructure_brief', language },
        });
        setScriptResult({ text: res.data.script, routing: res.data.routing, translation: res.data.translation, original: res.data.original_english, assessment: res.data.assessment });
      } else if (mode === 'explain') {
        const q = question.trim() || `What is the current status of ${insight.domain?.replace(/_/g, ' ')}?`;
        res = await api.post('/api/vega/explain', null, {
          params: { question: q, domain: insight.domain, language },
        });
        setScriptResult({ text: res.data.answer, meta: res.data.meta, translation: res.data.translation, assessment: res.data.assessment });
      } else if (mode === 'compare') {
        res = await api.post('/api/vega/compare', null, {
          params: { domain: insight.domain, language },
        });
        setScriptResult({ text: res.data.comparison, meta: res.data.meta, translation: res.data.translation, assessment: res.data.assessment });
      } else if (mode === 'watch') {
        res = await api.post('/api/vega/what-to-watch', null, {
          params: { domain: insight.domain, language },
        });
        setScriptResult({ text: res.data.watch_items, meta: res.data.meta, translation: res.data.translation, assessment: res.data.assessment });
      }
    } catch (err) {
      setError(getApiErrorMessage(err, 'Generation failed'));
    } finally {
      setGenerating(false);
    }
  }, [insight, mode, language, question]);

  const handleCopy = useCallback(() => {
    if (scriptResult?.text) {
      navigator.clipboard.writeText(scriptResult.text);
    }
  }, [scriptResult]);

  if (!insight) {
    return (
      <div className="vega-right" data-testid="vega-right-panel">
        <div className="vega-no-selection" data-testid="vega-no-selection">AWAITING SELECTION</div>
      </div>
    );
  }

  const alertColor = ALERT_COLORS[insight.alert] || '#818CF8';
  const dp = insight.data_points || {};
  const dataEntries = Object.entries(dp).filter(([k]) => !['status', 'note'].includes(k));

  return (
    <div className="vega-right" data-testid="vega-right-panel">
      <div className="vega-detail" data-testid="vega-detail">
        {/* Header */}
        <div className="vega-detail-header">
          <div className="vega-detail-row1">
            <span className="vega-detail-badge" style={{ color: alertColor, borderColor: `${alertColor}60` }} data-testid="vega-detail-badge">
              {insight.alert}
            </span>
            <span className="vega-detail-domain" data-testid="vega-detail-domain">
              {insight.domain?.replace(/_/g, ' ')}
            </span>
          </div>
          <div className="vega-detail-topic" data-testid="vega-detail-topic">{insight.topic}</div>
          <div className="vega-detail-ts" data-testid="vega-detail-timestamp">{insight.timestamp}</div>
        </div>

        {/* Data points */}
        {dataEntries.length > 0 && (
          <div className="vega-detail-block" data-testid="vega-data-points">
            <div className="vega-section-label">DATA POINTS</div>
            <div className="vega-data-grid">
              {dataEntries.slice(0, 8).map(([k, v]) => (
                <div className="vega-data-item" key={k}>
                  <span className="vega-data-label">{k.replace(/_/g, ' ')}</span>
                  <span className="vega-data-value">{typeof v === 'object' ? JSON.stringify(v) : String(v)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Confidence meters */}
        <div className="vega-detail-block" data-testid="vega-meters">
          <div className="vega-section-label">ASSESSMENT</div>
          <div className="vega-meters">
            <div className="vega-meter">
              <span className="vega-meter-label">SEVERITY</span>
              <div className="vega-bar-track"><div className="vega-bar-fill" style={{ width: `${(insight.severity || 0) * 100}%`, background: alertColor }} /></div>
              <span className="vega-meter-val" style={{ color: alertColor }}>{((insight.severity || 0) * 100).toFixed(0)}%</span>
            </div>
            <div className="vega-meter">
              <span className="vega-meter-label">CONFIDENCE</span>
              <div className="vega-bar-track"><div className="vega-bar-fill" style={{ width: `${(insight.confidence || 0) * 100}%`, background: '#6366F1' }} /></div>
              <span className="vega-meter-val" style={{ color: '#6366F1' }}>{((insight.confidence || 0) * 100).toFixed(0)}%</span>
            </div>
          </div>
        </div>

        {/* Operational Impact */}
        {insight.impact && (
          <div className="vega-detail-block" data-testid="vega-operational-impact">
            <div className="vega-section-label">OPERATIONAL IMPACT</div>
            <div className="vega-assessment-body">{insight.impact}</div>
          </div>
        )}

        {/* Sources */}
        <div className="vega-detail-block" data-testid="vega-sources">
          <div className="vega-section-label">SOURCES</div>
          <div className="vega-sources">
            {(insight.sources || []).map((s, i) => (
              <span className="vega-src-tag" key={i}>{s}</span>
            ))}
          </div>
        </div>

        {/* Affected systems */}
        {insight.affected_systems?.length > 0 && (
          <div className="vega-detail-block" data-testid="vega-affected-systems">
            <div className="vega-section-label">AFFECTED SYSTEMS</div>
            <div className="vega-sources">
              {insight.affected_systems.map((s, i) => (
                <span className="vega-src-tag" key={i} style={{ color: '#F59E0B', borderColor: '#F59E0B40', background: '#F59E0B08' }}>{s}</span>
              ))}
            </div>
          </div>
        )}

        {/* Mode tabs */}
        <div className="vega-detail-block" data-testid="vega-generation-section">
          <div className="vega-section-label">INTELLIGENCE OUTPUT</div>
          <div className="vega-mode-tabs" data-testid="vega-mode-tabs">
            {MODES.map(m => (
              <button
                key={m.key}
                className={`vega-mode-tab ${mode === m.key ? 'active' : ''}`}
                onClick={() => { setMode(m.key); setScriptResult(null); setError(null); }}
                data-testid={`vega-mode-${m.key}`}
              >
                {m.label}
              </button>
            ))}
          </div>

          {/* Question input for explain mode */}
          {mode === 'explain' && (
            <input
              type="text"
              value={question}
              onChange={e => setQuestion(e.target.value)}
              placeholder="Ask about infrastructure status..."
              style={{
                width: '100%', fontFamily: 'var(--v-mono)', fontSize: '10px',
                color: 'var(--v-text)', background: 'var(--v-bg)', border: '1px solid var(--v-border)',
                padding: '8px 10px', marginBottom: '8px', borderRadius: '2px', letterSpacing: '1px',
                outline: 'none',
              }}
              data-testid="vega-question-input"
            />
          )}

          <button
            className="vega-gen-btn"
            onClick={handleGenerate}
            disabled={generating}
            data-testid="vega-generate-btn"
          >
            {generating ? 'GENERATING...' : `GENERATE ${MODES.find(m => m.key === mode)?.label || ''}`}
          </button>

          {error && <div className="vega-gen-error" data-testid="vega-gen-error">{error}</div>}

          {scriptResult && (
            <div className="vega-script-output" data-testid="vega-script-output">
              {/* Routing metadata */}
              {scriptResult.routing && (
                <div className="vega-routing-meta" data-testid="vega-routing-meta">
                  <span className="vega-routing-model">{scriptResult.routing.selectedModel}</span>
                  <span className="vega-routing-sep">/</span>
                  <span className="vega-routing-reason">{scriptResult.routing.routingReasonLabel}</span>
                  {scriptResult.routing.fallbackUsed && (
                    <span className="vega-routing-fallback">FALLBACK</span>
                  )}
                </div>
              )}
              {scriptResult.meta && (
                <div className="vega-routing-meta" data-testid="vega-meta-info">
                  <span className="vega-routing-model">{scriptResult.meta.model}</span>
                  {scriptResult.meta.fallback && (
                    <span className="vega-routing-fallback">FALLBACK</span>
                  )}
                </div>
              )}

              <div className="vega-script-text" data-testid="vega-script-text">{scriptResult.text}</div>

              <div className="vega-script-meta">
                <span data-testid="vega-script-language">LANG: {language.toUpperCase()}</span>
                <button className="vega-copy-btn" onClick={handleCopy} data-testid="vega-copy-btn">COPY</button>
              </div>

              {/* Translation metadata */}
              {scriptResult.translation && scriptResult.translation.translation_confidence > 0 && (
                <div className="vega-translation-meta" data-testid="vega-translation-meta">
                  TRANSLATED ({scriptResult.translation.method}) / CONFIDENCE: {(scriptResult.translation.translation_confidence * 100).toFixed(0)}%
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
